import { useState, useEffect, useRef, memo } from 'react';
import { Box } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import PropTypes from 'prop-types';
import { apiUrl } from 'config';
import { getToken } from 'utils/session';

import {
  ClassicEditor,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontSize,
  Highlight,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  LinkImage,
  Paragraph,
  Indent,
  IndentBlock,
  TextTransformation,
  Underline,
  Undo,
  SimpleUploadAdapter,
} from 'ckeditor5';

import FullScreen from './component/Fullscreen';
import ResizableHeight from './component/Resizable';

function Editor({
  key = 0,
  height = 150,
  disabled = false,
  placeholder = '',
  initValue,
  onChange,
  onInit,
}) {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const editorConfig = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'fontSize',
        '|',
        'bold',
        'italic',
        'underline',
        '|',
        'alignment',
        '|',
        'outdent',
        'indent',
        '|',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'link',
        'fullscreen',
      ],
      shouldNotGroupWhenFull: true,
    },
    plugins: [
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      Bold,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontSize,
      FullScreen,
      Highlight,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      ImageInsert,
      Italic,
      Link,
      LinkImage,
      Paragraph,
      TextTransformation,
      Underline,
      Undo,
      Indent,
      IndentBlock,
      ResizableHeight,
      SimpleUploadAdapter,
    ],
    fontSize: {
      options: [8, 10, 12, 14, 18, 24, 36],
      supportAllValues: true,
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    initialData: `${initValue ?? ''}`,
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        openInNewTab: {
          mode: 'manual',
          label: 'Open in a new tab',
          defaultValue: true, // This option will be selected by default.
          attributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        },
      },
    },
    simpleUpload: {
      uploadUrl: `${apiUrl}/admin/task-comment/upload`,

      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
    ResizableHeight: {
      resize: true,
      height: `${height}px`,
      minHeight: `${height}px`,
      maxHeight: '2500px',
    },
    placeholder: placeholder,
  };

  return (
    <Box key={key}>
      <Box className="main-container">
        <Box
          className="editor-container editor-container_classic-editor"
          ref={editorContainerRef}
        >
          <Box className="editor-container__editor">
            <Box ref={editorRef}>
              {isLayoutReady && (
                <CKEditor
                  editor={ClassicEditor}
                  config={editorConfig}
                  onReady={onInit}
                  onChange={onChange}
                  disabled={disabled}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

Editor.propTypes = {
  key: PropTypes.number,
  height: PropTypes.number,
  disabled: PropTypes.boolean,
  placeholder: PropTypes.string,
  initValue: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  onInit: PropTypes.func,
};

export default memo(Editor);
